/**
 * Colour theme (light / dark) for the app surfaces — the my-account area.
 *
 * Mirrors WeHunt Web: an explicit choice, no system-preference fallback
 * (exactly as in the prototype), applied as `data-theme="dark"` on <html>.
 *
 * The choice is shared with WeHunt Web, which lives on another domain, so a
 * cookie cannot carry it across. Instead:
 *  - every link into WeHunt Web carries `?theme=light|dark` (withThemeParam),
 *  - a `?theme=` on any URL we are opened with is consumed at boot
 *    (consumeThemeFromUrl) — that is how the choice comes back from the web app,
 *  - the choice is persisted in localStorage AND a `wh-theme` cookie on this
 *    site, so it survives a cleared storage and is readable server-side.
 * The inline script in index.html reads the same three sources before first
 * paint, so a dark page never flashes light.
 *
 * A tiny external store rather than a context: the toggle lives in two places
 * (the account sidebar and the header menu) and both must agree without
 * threading a provider through the tree.
 */

import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

/** Same key as WeHunt Web (`wh-theme`), for storage, the cookie and the URL contract. */
export const THEME_STORAGE_KEY = "wh-theme";
export const THEME_COOKIE_NAME = "wh-theme";
/** Query parameter both apps use to hand the choice across the domain boundary. */
export const THEME_QUERY_PARAM = "theme";

const THEME_ATTRIBUTE = "data-theme";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const listeners = new Set<() => void>();
let currentTheme: Theme | null = null;

export function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function readLocalStorageTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function readCookieTheme(
  cookieString: string = document.cookie
): Theme | null {
  const match = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${THEME_COOKIE_NAME}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.slice(THEME_COOKIE_NAME.length + 1));
  return isTheme(value) ? value : null;
}

/**
 * The stored choice: localStorage first, the cookie second; light when neither
 * holds a valid value or storage is unavailable.
 */
export function readStoredTheme(): Theme {
  return readLocalStorageTheme() ?? readCookieTheme() ?? "light";
}

function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage unavailable (private mode, quota): the cookie still carries it.
  }
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${THEME_COOKIE_NAME}=${theme}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Cookies disabled: the choice still applies for this page view.
  }
}

/** Sets or clears the attribute the dark stylesheet keys off. */
export function applyThemeToDocument(theme: Theme, doc: Document = document) {
  if (theme === "dark") {
    doc.documentElement.setAttribute(THEME_ATTRIBUTE, "dark");
  } else {
    doc.documentElement.removeAttribute(THEME_ATTRIBUTE);
  }
}

export function getTheme(): Theme {
  if (currentTheme === null) currentTheme = readStoredTheme();
  return currentTheme;
}

export function setTheme(theme: Theme): void {
  currentTheme = theme;
  persistTheme(theme);
  applyThemeToDocument(theme);
  listeners.forEach((listener) => listener());
}

export function toggleTheme(): void {
  setTheme(getTheme() === "dark" ? "light" : "dark");
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Appends the theme to a URL the other app will open, keeping any query it has. */
export function withThemeParam(url: string, theme: Theme): string {
  const [base, hash = ""] = splitHash(url);
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${THEME_QUERY_PARAM}=${theme}${hash}`;
}

function splitHash(url: string): [string, string] {
  const index = url.indexOf("#");
  return index === -1 ? [url, ""] : [url.slice(0, index), url.slice(index)];
}

/**
 * Adopts a `?theme=` the page was opened with (the web app handing the choice
 * back), persists it and strips it from the address bar so it is not carried
 * into every later link or bookmark. Returns the adopted theme, or null when
 * the URL carried none. Call once at boot, before rendering.
 */
export function consumeThemeFromUrl(win: Window = window): Theme | null {
  const params = new URLSearchParams(win.location.search);
  const value = params.get(THEME_QUERY_PARAM);
  if (!isTheme(value)) return null;

  setTheme(value);

  params.delete(THEME_QUERY_PARAM);
  const search = params.toString();
  const cleaned = `${win.location.pathname}${search ? `?${search}` : ""}${win.location.hash}`;
  win.history.replaceState(win.history.state, "", cleaned);
  return value;
}

/** Test seam: forget the cached choice so the next read hits storage again. */
export function resetThemeForTests(): void {
  currentTheme = null;
}

function getServerTheme(): Theme {
  return "light";
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, getServerTheme);
  return {
    theme,
    isDark: theme === "dark",
    setTheme,
    toggleTheme,
  };
}
